import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import type {
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount
} from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'BrokerTsLib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import { getProductFullName } from 'BrokerTsLib/src/enums'
import { useUpdateDiscount } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useRef } from 'react'

import { DiscountEditForm, type DiscountEditFormValues } from './DiscountEditForm'

export const DiscountEditModal = ({
  discount,
  discounts,
  insurer,
  isOpen,
  onClose,
}: {
  discount: InsurerOrganizationUnitDiscount | InsuranceCategoryDiscount
  discounts: InsuranceCategoryDiscount[]
  insurer: InsurerOrganizationUnit
  isOpen: boolean
  onClose: () => void
}) => {

  const methods = useForm<DiscountEditFormValues>({
    defaultValues: {
      ...discount,
      applyToSubordinates: false
    },
  })

  const { handleSubmit, reset, formState: { isDirty } } = methods
  const submitDialogLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const {
    isOpen: isSubmitDialogOpen,
    onClose: closeSubmitDialog,
    onOpen: openSubmitDialog,
  } = useDisclosure()
  const toast = useToast()
  const { mutateAsync: updateDiscount, isPending: isUpdating } = useUpdateDiscount()

  const handleSave = async (value: DiscountEditFormValues) => {
    try {
      const r = await updateDiscount({
        organizationUnitId: insurer.organizationUnitId,
        insurerId: insurer.insurerId,
        insuranceCategoryType: insurer.insuranceCategoryType,
        ...value,
      })

      toast({
        title: 'Sleva byla uložena',
        status: 'success',
      })

      reset({...value, applyToSubordinates: false})

      return r
    } catch (e) {
      toast({
        title: 'Chyba při ukládání slevy',
        status: 'error',
      })

      return e
    }
  }

  const handleClose = () => {
    if (isDirty) {
      openSubmitDialog()
    } else {
      onClose()
    }
  }

  const conflictDiscounts = discount.conflictDiscountIds
    .map((cd) => discounts.find((d) => d.insuranceCategoryDiscountId === cd))
    .filter((d) => !!d)

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <Text sx={{ my: 1 }} fontSize='md' fontWeight='medium'>
              {insurer.insurer} - {getProductFullName(insurer.insuranceCategoryType)}
            </Text>

            {discount.name && <Text my={2} fontSize='xl' fontWeight='semibold'>{discount.name}</Text>}

            {conflictDiscounts.length > 0 && <ConflictDiscounts conflicts={conflictDiscounts} />}
            
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody sx={{ pt: 0 }}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(handleSave)}>
                <DiscountEditForm {...discount}/>
              </form>
            </FormProvider>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isSubmitDialogOpen}
        onClose={closeSubmitDialog}
        leastDestructiveRef={submitDialogLeastDestructiveRef}
      >
        <AlertDialogOverlay />

        <AlertDialogContent alignSelf="center">
          <AlertDialogHeader>Neuložené změny</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            Přejete si uložit změny nebo je zahodit?
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button
              colorScheme="red"
              mr={'auto'}
              onClick={() => {
                reset(discount)
                closeSubmitDialog()
                onClose()
              }}
              isDisabled={isUpdating}
            >
              Zahodit změny
            </Button>
            <Button
              ref={submitDialogLeastDestructiveRef}
              onClick={closeSubmitDialog}
              isDisabled={isUpdating}
              colorScheme='gray'
            >
              Zpět
            </Button>
            <Button
              colorScheme="primary"
              onClick={() =>
                handleSubmit((data) => handleSave(data).then(onClose))()
                  .finally(() => closeSubmitDialog())
              }
              isLoading={isUpdating}
            >
              Uložit změny
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog >
    </>
  )
}

const ConflictDiscounts = ({
  conflicts,
}: {
  conflicts: InsuranceCategoryDiscount[]
}) => {
  return (
    <Flex fontSize="sm" fontWeight="normal" whiteSpace="pre-wrap" flexFlow="wrap">
      <Text>Nelze kombinovat s:</Text>
      {conflicts.map((conflict, index) => (
        <Text key={index}>
          {` ${conflict.name}`}
          {index < conflicts.length - 1 && ','}
        </Text>
      ))}
    </Flex>
  )
}